import { RadioGroup } from 'radix-ui';
import { cn } from '@/shared/lib';

export interface SegmentItem {
  value: string;
  label: string;
}

/**
 * Последний сегмент-действие (главная: «Установить период» открывает окно выбора дат): обычная
 * кнопка в том же ряду, не вариант выбора; `active` — сегмент выглядит выбранным (свой период).
 */
export interface SegmentAction {
  label: string;
  active: boolean;
  onClick: () => void;
}

export interface SegmentedControlProps {
  value: string;
  onValueChange: (value: string) => void;
  items: readonly SegmentItem[];
  /**
   * registry — быстрые фильтры реестра (README §3; активный сегмент как в прототипе:
   *            фон gold-wash-2, кольцо gold-soft, 600 ink-900);
   * dashboard — сегменты периода дашборда (README §2: активный — белый с тенью);
   * floating — плавающий переключатель языка World (README §1: активная — wine-ink).
   * compact — переключатель языка ввода поля LocalizedText в строке подписи: те же цвета, что у
   *           floating (активная — wine-ink), размер под строку подписи (18 / 10 600).
   * toolbar — фильтр в панели экрана «Уведомления» (README §7): как registry по размерам,
   *           активный сегмент белый с тенью сегмента, 600 ink-900.
   * period — периоды шапки главной (прототип `periods`): 26 / padding 0 11 / radius 16, 11.5 500,
   *          неактивный ink-500, активный — белый, тень сегмента с кольцом line-hair, ink-900.
   * view — вид главной администратора «Обзор / Динамика» (прототип `dashViews`): padding 0 13,
   *        11.5 600, неактивный ink-600, активный — белый с тенью сегмента без кольца.
   */
  variant?: 'registry' | 'dashboard' | 'floating' | 'compact' | 'toolbar' | 'period' | 'view';
  /** Сегмент-действие в конце ряда (только у варианта period). */
  action?: SegmentAction;
  'aria-label': string;
  className?: string;
}

const CONTAINER_CLASSES = {
  registry: 'gap-2 rounded-16 border border-line-strong bg-surface-sunken p-2',
  dashboard: 'gap-2 rounded-18 bg-surface-sunken p-3',
  floating: 'gap-8 rounded-20 bg-paper-pure p-4 shadow-lang',
  compact: 'gap-2 rounded-11 bg-surface-sunken p-2',
  toolbar: 'gap-2 rounded-16 border border-line-strong bg-surface-sunken p-2',
  period: 'gap-2 rounded-18 bg-surface-sunken p-3',
  view: 'gap-2 rounded-18 bg-surface-sunken p-3',
} as const;

const ITEM_CLASSES = {
  registry: [
    'h-26 rounded-9 px-10 text-[12px] font-medium text-ink-600',
    'data-[state=checked]:bg-gold-wash-2 data-[state=checked]:font-semibold data-[state=checked]:text-ink-900',
    'data-[state=checked]:shadow-[inset_0_0_0_1px_var(--vw-gold-soft)]',
  ].join(' '),
  dashboard: [
    'h-26 rounded-16 px-11 text-[11.5px] font-medium text-ink-600',
    'data-[state=checked]:bg-paper-pure data-[state=checked]:text-ink-900 data-[state=checked]:shadow-segment-on',
  ].join(' '),
  floating: [
    'h-28 min-w-36 rounded-14 px-10 text-[12px] font-semibold tracking-[0.04em] text-ink-600',
    'data-[state=checked]:bg-wine-ink data-[state=checked]:text-ink-inverse',
  ].join(' '),
  compact: [
    'h-18 min-w-28 rounded-9 px-6 text-[10px] font-semibold tracking-[0.04em] text-ink-600',
    'data-[state=checked]:bg-wine-ink data-[state=checked]:text-ink-inverse',
  ].join(' '),
  toolbar: [
    'h-26 rounded-9 px-10 text-[12px] font-medium text-ink-600',
    'data-[state=checked]:bg-paper-pure data-[state=checked]:font-semibold data-[state=checked]:text-ink-900',
    'data-[state=checked]:shadow-segment-on',
  ].join(' '),
  period: [
    'h-26 rounded-16 px-11 text-[11.5px] font-medium text-ink-500',
    'data-[state=checked]:bg-paper-pure data-[state=checked]:text-ink-900 data-[state=checked]:shadow-segment-on',
  ].join(' '),
  view: [
    'h-26 rounded-16 px-13 text-[11.5px] font-semibold text-ink-600',
    'data-[state=checked]:bg-paper-pure data-[state=checked]:text-ink-900 data-[state=checked]:shadow-segment',
  ].join(' '),
} as const;

const ITEM_BASE =
  'flex cursor-pointer items-center justify-center whitespace-nowrap transition-colors duration-[140ms]';

/** Сегменты — role="radiogroup" (README «Доступность»), навигация стрелками — Radix. */
export function SegmentedControl({
  value,
  onValueChange,
  items,
  variant = 'registry',
  action,
  className,
  ...aria
}: SegmentedControlProps) {
  const radios = items.map((item) => (
    <RadioGroup.Item
      key={item.value}
      value={item.value}
      className={cn(ITEM_BASE, ITEM_CLASSES[variant])}
    >
      {item.label}
    </RadioGroup.Item>
  ));
  if (!action) {
    return (
      <RadioGroup.Root
        value={value}
        onValueChange={onValueChange}
        orientation="horizontal"
        className={cn('inline-flex items-center', CONTAINER_CLASSES[variant], className)}
        {...aria}
      >
        {radios}
      </RadioGroup.Root>
    );
  }
  // Действие — кнопка рядом с группой вариантов (не radio): в radiogroup допустимы только radio.
  return (
    <div className={cn('inline-flex items-center', CONTAINER_CLASSES[variant], className)}>
      <RadioGroup.Root
        value={value}
        onValueChange={onValueChange}
        orientation="horizontal"
        className="flex items-center gap-2"
        {...aria}
      >
        {radios}
      </RadioGroup.Root>
      <button
        type="button"
        data-state={action.active ? 'checked' : 'unchecked'}
        aria-pressed={action.active}
        onClick={action.onClick}
        className={cn(ITEM_BASE, 'border-0 bg-transparent font-ui', ITEM_CLASSES[variant])}
      >
        {action.label}
      </button>
    </div>
  );
}
